import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { FormField } from '@/components/auth/FormField';
import { SubmitButton } from '@/components/auth/SubmitButton';
import { AuthError } from '@/components/auth/AuthError';
import { AuthSuccess } from '@/components/auth/AuthSuccess';
import { supabase } from '@/lib/supabase';
import { describeAuthError } from '@/lib/authErrors';
import {
  validateEmail,
  validatePassword,
  validatePasswordMatch,
  validateUsername,
} from '@/lib/validation';

interface FieldErrors {
  username?: string;
  email?: string;
  password?: string;
  confirm?: string;
}

export default function SignupPage(): JSX.Element {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSubmitError(null);

    const errors: FieldErrors = {};
    const usernameError = validateUsername(username);
    if (usernameError) errors.username = usernameError;
    const emailError = validateEmail(email);
    if (emailError) errors.email = emailError;
    const passwordError = validatePassword(password);
    if (passwordError) errors.password = passwordError;
    const matchError = validatePasswordMatch(password, confirm);
    if (matchError) errors.confirm = matchError;

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    setLoading(true);

    const trimmedUsername = username.trim();
    const trimmedEmail = email.trim();

    try {
      // Check username availability before calling signUp, so we don't
      // create an auth.users row that can never get a profile due to the
      // UNIQUE conflict on profiles.username inside handle_new_user.
      const { data: available, error: rpcError } = await supabase.rpc('is_username_available', {
        candidate: trimmedUsername,
      });
      if (rpcError) {
        setSubmitError(
          'Could not verify username availability. Check your connection and try again.',
        );
        return;
      }
      if (available === false) {
        setFieldErrors({ username: 'That name is already taken.' });
        return;
      }

      const { error } = await supabase.auth.signUp({
        email: trimmedEmail,
        password,
        options: {
          data: { username: trimmedUsername },
          emailRedirectTo: `${window.location.origin}/confirm-email`,
        },
      });

      if (error) {
        setSubmitError(describeAuthError(error));
        return;
      }

      setSubmittedEmail(trimmedEmail);
    } catch (err) {
      setSubmitError(describeAuthError(err));
    } finally {
      setLoading(false);
    }
  }

  if (submittedEmail) {
    return (
      <AuthLayout
        title="Check your inbox"
        subtitle="One step left before you set sail."
        footer={
          <>
            Already confirmed?{' '}
            <Link to="/login" className="text-ink underline hover:text-candle-dark">
              Sign in
            </Link>
          </>
        }
      >
        <AuthSuccess>
          We sent a confirmation link to <strong className="font-semibold">{submittedEmail}</strong>
          . Click it to verify your account, then come back here to sign in.
        </AuthSuccess>
        <p className="text-sm text-ink-mid leading-relaxed">
          The email should arrive within a minute. If you do not see it, check your spam folder.
          Confirmation links expire after 24 hours.
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Enlist"
      subtitle="Create an account to start your pirate's tale."
      footer={
        <>
          Already enlisted?{' '}
          <Link to="/login" className="text-ink underline hover:text-candle-dark">
            Sign in
          </Link>
        </>
      }
    >
      <AuthError message={submitError} />

      <form onSubmit={handleSubmit} noValidate>
        <FormField
          name="username"
          label="Username"
          type="text"
          autoComplete="username"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          inputMode="text"
          maxLength={20}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          hint="3-20 characters. Letters, numbers, underscores."
          error={fieldErrors.username}
          required
        />

        <FormField
          name="email"
          label="Email"
          type="email"
          autoComplete="email"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          inputMode="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={fieldErrors.email}
          required
        />

        <FormField
          name="password"
          label="Password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          hint="At least 8 characters and one number."
          error={fieldErrors.password}
          required
        />

        <FormField
          name="confirm"
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          error={fieldErrors.confirm}
          required
        />

        <SubmitButton loading={loading}>{loading ? 'Enlisting…' : 'Enlist'}</SubmitButton>
      </form>
    </AuthLayout>
  );
}
